import { ResponsiveLine } from '@nivo/line';
import classNames from 'classnames';
import Papa from 'papaparse';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '../../components/form/Select';
import { TwTheme } from '../../Tw';
import { getBestContrastColor } from '../common/color.helper';
import { formatInteger } from '../common/formatHelper';
import { cn } from '../common/utils';
import { Container } from '../components/layout/ContainerFlex';
import { Stat } from '../components/stats/Stat';
import { StatGroup } from '../components/stats/StatGroup';
import { StatListItem } from '../components/stats/StatListItem';
import { Table } from '../components/table/Table';
import {
  ABC_COLORS,
  ABC_WEIGHTS,
  ABC_XYZ_COLORS,
  getABCXYZCategory,
  getABCXYZMatrix,
  getWeeksFromData,
  getXYZClassification,
  SKUData,
  XYZ_COLORS,
} from './sku-profiling';
import { useSKU } from './SKUContext';

const categoryABCXYZ = TwTheme.extend.colors.categoryABCXYZ;

interface AccuracyMetrics {
  linesAccuracy: number;
  quantityAccuracy: number;
  abcAccuracy: number;
}

// const ABC_COLORS = {
//   'A+': categoryABCXYZ.S,
//   A: categoryABCXYZ.A,
//   B: categoryABCXYZ.B,
//   C: categoryABCXYZ.C,
//   Unknown: categoryABCXYZ.unknown,
// };

// const XYZ_COLORS = {
//   X: categoryABCXYZ.X,
//   Y: categoryABCXYZ.Y,
//   Z: categoryABCXYZ.Z,
// };

// const XYZ_DESCRIPTIONS = {
//   X: 'Stable demand',
//   Y: 'Variable demand',
//   Z: 'Highly variable demand',
// };

// const ABC_XYZ_COLORS = {
//   Strategic: categoryABCXYZ.strategic,
//   Critical: categoryABCXYZ.critical,
//   Regular: categoryABCXYZ.regular,
//   Review: categoryABCXYZ.review,
//   'Non-critical': categoryABCXYZ.nonCritical,
//   'Dead-Stock': categoryABCXYZ.deadStock,
// };

// const ABC_XYZ_MATRIX = {
//   'A+': { X: 'Strategic', Y: 'Critical', Z: 'Review' },
//   A: { X: 'Strategic', Y: 'Critical', Z: 'Review' },
//   B: { X: 'Regular', Y: 'Regular', Z: 'Review' },
//   C: { X: 'Regular', Y: 'Review', Z: 'Non-critical' },
//   //   D: { X: 'Dead-Stock', Y: 'Dead-Stock', Z: 'Dead-Stock' },
// };

// const getXYZClassification = (
//   data: SKUData,
//   week: string,
//   forecast = false,
// ) => {
//   const prefix = forecast ? 'fc_' : '';
//   const value = data[`w${week}_${prefix}lines`];
//   const cv = value / data.max_lines;

//   if (cv <= 0.5) return 'X';
//   if (cv <= 1) return 'Y';
//   return 'Z';
// };

type MetricType = 'ABC' | 'XYZ' | 'COMBINED' | 'LINES' | 'QUANTITIES';

const SKUAnalysisView: React.FC = () => {
  const { t } = useTranslation('app');

  const { selectedSKU, setSelectedSKU, skuData, setSkuData } = useSKU();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('LINES');
  const [selectedFileName, setSelectedFileName] = useState<string>('');

  // Calculate metrics for selected SKU
  const selectedSKUData = useMemo(() => {
    if (!selectedSKU || !skuData.length) return null;
    return skuData.find(sku => sku.sku === selectedSKU);
  }, [selectedSKU, skuData]);

  // Calculate XYZ classification
  const xyzClassification = useMemo(() => {
    if (!selectedSKUData) return null;

    const weeks = getWeeksFromData(selectedSKUData);
    const values = weeks.map(week => Number(selectedSKUData[`w${week}_lines`]));

    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance =
      values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
    const cv = Math.sqrt(variance) / mean;

    if (cv <= 0.5) return { class: 'X', cv };
    if (cv <= 1) return { class: 'Y', cv };
    return { class: 'Z', cv };
  }, [selectedSKUData]);

  // Calculate accuracy metrics
  const accuracyMetrics = useMemo(() => {
    if (!selectedSKUData) return null;

    const weeks = getWeeksFromData(selectedSKUData);
    let totalLinesDiff = 0;
    let totalQuantityDiff = 0;
    let abcMatches = 0;
    let xyzMatches = 0;
    let combinedMatches = 0;
    let weightedAbcError = 0;

    // Calculate min, max, avg for lines and quantities
    const actualLines = weeks.map(week =>
      Number(selectedSKUData[`w${week}_lines`]),
    );
    const actualQty = weeks.map(week =>
      Number(selectedSKUData[`w${week}_quantity`]),
    );

    const stats = {
      lines: {
        min: Math.min(...actualLines),
        max: Math.max(...actualLines),
        avg: actualLines.reduce((a, b) => a + b, 0) / actualLines.length,
      },
      quantity: {
        min: Math.min(...actualQty),
        max: Math.max(...actualQty),
        avg: actualQty.reduce((a, b) => a + b, 0) / actualQty.length,
      },
    };

    const baselineLines = stats.lines.max;
    const baselineQty = stats.quantity.max;

    weeks.forEach(week => {
      const actualLines = Number(selectedSKUData[`w${week}_lines`]);
      const forecastLines = Number(selectedSKUData[`w${week}_fc_lines`]);
      totalLinesDiff += Math.abs((actualLines - forecastLines) / actualLines);

      const actualQty = Number(selectedSKUData[`w${week}_quantity`]);
      const forecastQty = Number(selectedSKUData[`w${week}_fc_quantity`]);
      totalQuantityDiff += Math.abs((actualQty - forecastQty) / actualQty);

      // ABC accuracy
      const abcMatch =
        selectedSKUData[`w${week}_abc`] === selectedSKUData[`w${week}_fc_abc`];
      if (abcMatch) abcMatches++;

      // XYZ accuracy
      const actualXYZ = getXYZClassification(selectedSKUData, week);
      const forecastXYZ = getXYZClassification(selectedSKUData, week, true);
      if (actualXYZ === forecastXYZ) xyzMatches++;

      // Combined ABC-XYZ accuracy
      if (abcMatch && actualXYZ === forecastXYZ) combinedMatches++;

      // Add weighted ABC error calculation
      const actualABC = selectedSKUData[`w${week}_abc`];
      const forecastABC = selectedSKUData[`w${week}_fc_abc`];
      const actualWeight = ABC_WEIGHTS[actualABC];
      const forecastWeight = ABC_WEIGHTS[forecastABC];

      // Calculate normalized differential error (0-1 scale)
      const maxPossibleDiff = Math.abs(ABC_WEIGHTS['A+'] - ABC_WEIGHTS['C']);
      const actualDiff = Math.abs(actualWeight - forecastWeight);
      const normalizedError = actualDiff / maxPossibleDiff;

      weightedAbcError += normalizedError;
    });

    return {
      linesAccuracy: (1 - totalLinesDiff / weeks.length) * 100,
      quantityAccuracy: (1 - totalQuantityDiff / weeks.length) * 100,
      abcAccuracy: (abcMatches / weeks.length) * 100,
      abcDifferentialAccuracy:
        ((weeks.length - weightedAbcError) / weeks.length) * 100,
      xyzAccuracy: (xyzMatches / weeks.length) * 100,
      combinedAccuracy: (combinedMatches / weeks.length) * 100,
      baselineLines,
      baselineQty,
      stats,
    };
  }, [selectedSKUData]);

  // Prepare line chart data
  const lineChartData = useMemo(() => {
    if (!selectedSKUData) return [];

    const weeks = getWeeksFromData(selectedSKUData);

    switch (selectedMetric) {
      case 'LINES':
        return [
          {
            id: 'Actual Lines',
            data: weeks.map(week => ({
              x: `Week ${week}`,
              y: selectedSKUData[`w${week}_lines`],
            })),
          },
          {
            id: 'Forecast Lines',
            data: weeks.map(week => ({
              x: `Week ${week}`,
              y: selectedSKUData[`w${week}_fc_lines`],
            })),
          },
        ];

      case 'QUANTITIES':
        return [
          {
            id: 'Actual Quantities',
            data: weeks.map(week => ({
              x: `Week ${week}`,
              y: selectedSKUData[`w${week}_quantity`],
            })),
          },
          {
            id: 'Forecast Quantities',
            data: weeks.map(week => ({
              x: `Week ${week}`,
              y: selectedSKUData[`w${week}_fc_quantity`],
            })),
          },
        ];

      case 'ABC':
        return [
          {
            id: 'ABC Classification',
            data: weeks.map(week => ({
              x: `Week ${week}`,
              y: selectedSKUData[`w${week}_abc`],
              color: ABC_COLORS[selectedSKUData[`w${week}_abc`]],
            })),
          },
          {
            id: 'ABC Forecast',
            data: weeks.map(week => ({
              x: `Week ${week}`,
              y: selectedSKUData[`w${week}_fc_abc`],
              color: ABC_COLORS[selectedSKUData[`w${week}_fc_abc`]],
            })),
          },
        ];

      case 'XYZ':
        return [
          {
            id: 'XYZ Classification',
            data: weeks.map(week => {
              const xyzClass = getXYZClassification(selectedSKUData, week);
              return {
                x: `Week ${week}`,
                y: xyzClass,
                color: XYZ_COLORS[xyzClass],
              };
            }),
          },
          {
            id: 'XYZ Forecast',
            data: weeks.map(week => {
              const xyzClass = getXYZClassification(
                selectedSKUData,
                week,
                true,
              );
              return {
                x: `Week ${week}`,
                y: xyzClass,
                color: XYZ_COLORS[xyzClass],
              };
            }),
          },
        ];

      case 'COMBINED':
        return [
          {
            id: 'Combined Classification',
            data: weeks.map(week => {
              const abc = selectedSKUData[`w${week}_abc`];
              const xyz = getXYZClassification(selectedSKUData, week);
              const category = getABCXYZCategory[abc]?.[xyz] || 'Review';
              return {
                x: `Week ${week}`,
                y: `${abc}-${xyz}`,
                color: ABC_XYZ_COLORS[category],
              };
            }),
          },
          {
            id: 'Combined Forecast',
            data: weeks.map(week => {
              const abc = selectedSKUData[`w${week}_fc_abc`];
              const xyz = getXYZClassification(selectedSKUData, week, true);
              const category = getABCXYZCategory[abc]?.[xyz] || 'Review';
              return {
                x: `Week ${week}`,
                y: `${abc}-${xyz}`,
                color: ABC_XYZ_COLORS[category],
              };
            }),
          },
        ];
    }
  }, [selectedSKUData, selectedMetric]);

  // Load and parse CSV data
  const loadCSVData = async (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: results => {
        const parsedData = results.data.map((row: any) => {
          const numericRow: any = {
            ...row,
            order_line_count: Number(row.order_line_count),
            quantity_ordered: Number(row.quantity_ordered),
            percent_rank: Number(row.percent_rank),
            cumulative_percent_rank: Number(row.cumulative_percent_rank),
            total_days_ordered: Number(row.total_days_ordered),
            total_weeks_ordered: Number(row.total_weeks_ordered),
            max_daily_lines_count: Number(row.max_daily_lines_count),
            min_daily_lines_count: Number(row.min_daily_lines_count),
            avg_daily_lines_count: Number(row.avg_daily_lines_count),
            max_weekly_lines_count: Number(row.max_weekly_lines_count),
            min_weekly_lines_count: Number(row.min_weekly_lines_count),
            avg_weekly_lines_count: Number(row.avg_weekly_lines_count),
            max_lines: Number(row.max_lines),
            max_quantity: Number(row.max_quantity),
            line_accuracy: Number(row.line_accuracy),
            rank: String(row.rank),
          };

          // Dynamically convert weekly metrics
          Object.keys(row).forEach(key => {
            if (key.startsWith('w') && key.includes('_')) {
              if (key.includes('abc')) {
                numericRow[key] = row[key]; // Keep as string for ABC classification
              } else {
                numericRow[key] = Number(row[key]);
              }
            }
          });

          return numericRow;
        });
        setSelectedSKU(parsedData[0].sku);
        setSkuData(parsedData as SKUData[]);
      },
    });
  };

  // Calculate ABC-XYZ matrix position
  const matrixPosition = useMemo(() => {
    if (!selectedSKUData || !xyzClassification) return null;

    const currentABC = selectedSKUData.rank;
    const xyzClass = xyzClassification.class;

    return {
      abc: currentABC,
      xyz: xyzClass,
      category: getABCXYZCategory[currentABC]?.[xyzClass] || 'Review',
    };
  }, [selectedSKUData, xyzClassification]);

  return (
    <Container
      col
      overflow
      componentName="SKUAnalysisViewContainer"
      className={classNames(
        'flex-1',
        // 'border-app-panel-dark -dark border ltr:border-l rtl:border-r',
        // 'p-4',
      )}
    >
      <div className="flex flex-col">
        {/* SKU Selection */}
        {/* <PanelHeader className="flex items-center gap-4">
          <FileUpload
            accept=".csv"
            onChange={file => {
              if (file) {
                setSelectedFileName(file.name);
                loadCSVData(file);
              }
            }}
            className={cn('rounded', 'px-4 py-2', 'bg-app-panel-dark/30')}
          >
            {selectedFileName || `Select File`}
          </FileUpload>
          <label className="text-sm font-medium">{t`Select Item:`}</label>
          <Select
            value={selectedSKU || ''}
            onChange={e => setSelectedSKU(e.target.value)}
            className="w-64"
          >
            <option value="">{t`SKU`}</option>
            {skuData.map(sku => (
              <option key={sku.sku} value={sku.sku}>
                {sku.sku}
              </option>
            ))}
          </Select>
        </PanelHeader> */}

        {selectedSKUData && (
          <>
            {/* Basic Info Card */}
            <header className="mx-1 mb-0.5 mt-1 grid grid-cols-3 gap-1">
              {/* <section className="col-span-2 grid grid-cols-2 gap-1"> */}
              <Stat
                inFilterStat
                title={t('Item: {{sku}}', { sku: selectedSKUData.sku })}
                className="col-span-1"
                titleClassName={'!text-2xl'}
              >
                <StatListItem
                  // multiline
                  title={t`Quantity Ordered:`}
                  value={formatInteger(selectedSKUData.quantity_ordered)}
                  valueClass={'!text-2xl'}
                  unitOfMeasure={`EA`}
                />

                <StatGroup
                  title={t`Location Stats`}
                  classNameTitleContainer={`!bg-transparent !px-0 pb-0 mt-2`}
                  classNameTitle={`!text-menu-text !text-sm`}
                  classNameStatGroupItems="flex gap-2"
                  horizontal
                >
                  <StatListItem
                    multiline
                    title={t`Pick`}
                    value={formatInteger(selectedSKUData.total_days_ordered)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    multiline
                    title={t`Storage`}
                    value={formatInteger(selectedSKUData.total_weeks_ordered)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    multiline
                    title={t`Temp`}
                    value={formatInteger(selectedSKUData.total_weeks_ordered)}
                    valueClass={'!text-2xl'}
                  />
                </StatGroup>
                <StatGroup
                  title={t`Replnishments`}
                  classNameTitleContainer={`!bg-transparent !px-0 pb-0 mt-2`}
                  classNameTitle={`!text-menu-text !text-sm`}
                  classNameStatGroupItems="flex gap-2"
                  horizontal
                >
                  <StatListItem
                    title={t`Min`}
                    multiline
                    value={formatInteger(selectedSKUData.min_daily_lines_count)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    title={t`Avg`}
                    multiline
                    value={formatInteger(selectedSKUData.avg_daily_lines_count)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    title={t`Max`}
                    multiline
                    value={formatInteger(selectedSKUData.max_daily_lines_count)}
                    valueClass={'!text-2xl'}
                  />
                </StatGroup>

                <StatGroup
                  title={t`Item Rank`}
                  classNameTitleContainer={`!bg-transparent !px-0 pb-0 mt-2`}
                  classNameTitle={`!text-menu-text !text-sm`}
                  classNameStatGroupItems="flex gap-2"
                  horizontal
                >
                  <StatListItem
                    multiline
                    title={t`ABC Rank:`}
                    value={selectedSKUData.rank}
                    valueClass={`!text-base rounded-full !p-1 m-0.5 flex-none w-8 h-8 flex items-center justify-center`}
                    valueStyle={{
                      backgroundColor: ABC_COLORS[selectedSKUData.rank],
                      color: getBestContrastColor(
                        ABC_COLORS[selectedSKUData.rank],
                      ),
                    }}
                  />
                  <StatListItem
                    multiline
                    title={t`XYZ Rank:`}
                    value={xyzClassification.class}
                    valueClass={`!text-base rounded-full !p-1 m-0.5 flex-none w-8 h-8 flex items-center justify-center`}
                    valueStyle={{
                      backgroundColor: XYZ_COLORS[xyzClassification.class],
                      color: getBestContrastColor(
                        XYZ_COLORS[xyzClassification.class],
                      ),
                    }}
                  />
                  <StatListItem
                    multiline
                    title={t`Repl. Rank:`}
                    value={xyzClassification.class}
                    valueClass={`!text-base rounded-full !p-1 m-0.5 flex-none w-8 h-8 flex items-center justify-center`}
                    valueStyle={{
                      backgroundColor: XYZ_COLORS[xyzClassification.class],
                      color: getBestContrastColor(
                        XYZ_COLORS[xyzClassification.class],
                      ),
                    }}
                  />
                </StatGroup>
              </Stat>

              <Stat inFilterStat title={t`Line Metrics`} className="col-span-1">
                <StatListItem
                  title={t`Total Lines:`}
                  value={formatInteger(selectedSKUData.order_line_count)}
                  valueClass={'!text-2xl'}
                />

                <StatGroup
                  title={t`Order Stats`}
                  classNameTitleContainer={`!bg-transparent !px-0 pb-0 mt-2`}
                  classNameTitle={`!text-menu-text !text-sm`}
                  classNameStatGroupItems="grid grid-cols-2 gap-2"
                  horizontal
                >
                  <StatListItem
                    multiline
                    title={t`Days Ordered:`}
                    value={formatInteger(selectedSKUData.total_days_ordered)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    multiline
                    title={t`Weeks Ordered:`}
                    value={formatInteger(selectedSKUData.total_weeks_ordered)}
                    valueClass={'!text-2xl'}
                  />
                </StatGroup>
                {/* Daily Lines Group */}
                <StatGroup
                  title={t`Daily Lines`}
                  classNameTitleContainer={`!bg-transparent !px-0 pb-0 mt-2`}
                  classNameTitle={`!text-menu-text !text-sm`}
                  classNameStatGroupItems="grid grid-cols-3 gap-2"
                  horizontal
                >
                  <StatListItem
                    title={t`Min`}
                    multiline
                    value={formatInteger(selectedSKUData.min_daily_lines_count)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    title={t`Avg`}
                    multiline
                    value={formatInteger(selectedSKUData.avg_daily_lines_count)}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    title={t`Max`}
                    multiline
                    value={formatInteger(selectedSKUData.max_daily_lines_count)}
                    valueClass={'!text-2xl'}
                  />
                </StatGroup>

                {/* Weekly Lines Group */}
                <StatGroup
                  title={t`Weekly Lines`}
                  classNameTitleContainer={`!bg-transparent !px-0 pb-0 mt-2`}
                  classNameTitle={`!text-menu-text !text-sm`}
                  classNameStatGroupItems="grid grid-cols-3 gap-2"
                  horizontal
                >
                  <StatListItem
                    title={t`Min`}
                    multiline
                    value={formatInteger(
                      selectedSKUData.min_weekly_lines_count,
                    )}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    title={t`Avg`}
                    multiline
                    value={formatInteger(
                      selectedSKUData.avg_weekly_lines_count,
                    )}
                    valueClass={'!text-2xl'}
                  />
                  <StatListItem
                    title={t`Max`}
                    multiline
                    value={formatInteger(
                      selectedSKUData.max_weekly_lines_count,
                    )}
                    valueClass={'!text-2xl'}
                  />
                </StatGroup>
              </Stat>
              {/* </section> */}

              {/* ABC-XYZ Matrix Position */}
              {matrixPosition && (
                <Stat className="col-span-1">
                  <h3 className="mb-2 text-lg font-semibold">
                    {/* ABC-XYZ Classification Matrix */}
                    <div
                      className="flex items-center gap-4 rounded-full px-1 py-0.5"
                      style={{
                        backgroundColor:
                          ABC_XYZ_COLORS[matrixPosition.category],
                        color: getBestContrastColor(
                          ABC_XYZ_COLORS[matrixPosition.category],
                        ),
                      }}
                    >
                      {/* <span
                        // className="h-6 w-6 rounded-full"
                        style={{
                          backgroundColor:
                            ABC_XYZ_COLORS[matrixPosition.category],
                        }}
                      ></span> */}
                      <span className="px-2 text-xl font-medium">
                        {matrixPosition.category}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-col justify-center px-4 py-1 text-lg">
                      <div className="text-menu-text text-sm">
                        {matrixPosition.category === 'Strategic' &&
                          t`High moving item with stable demand`}
                        {matrixPosition.category === 'Critical' &&
                          t`High moving item with variable demand`}
                        {matrixPosition.category === 'Regular' &&
                          t`Medium moving item`}
                        {matrixPosition.category === 'Review' &&
                          t`Items requiring strategy review`}
                        {matrixPosition.category === 'Non-critical' &&
                          t`Low value item with high variability`}
                      </div>
                    </div>
                  </h3>
                  <div
                    data-component="RankGridSelector"
                    className="flex flex-col"
                  >
                    <div className="w-full flex-1">
                      <table className="border-menu-header w-full border-collapse text-sm">
                        <thead>
                          <tr>
                            <th className="text-xxs p-2 text-center">
                              {t`Class`}
                            </th>
                            {['X', 'Y', 'Z'].map(xyz => (
                              <th
                                key={xyz}
                                className={cn(
                                  'relative',
                                  'h-12 w-12',
                                  'p-2',
                                  'text-center',
                                  matrixPosition.xyz === xyz
                                    ? 'bg-app-header-menu'
                                    : '',
                                )}
                              >
                                {matrixPosition.xyz === xyz ? (
                                  <div className="flex items-center justify-center">
                                    <div
                                      className={cn(
                                        'h-7 w-7',
                                        'rounded-full',
                                        // 'bg-menu-active text-menu-active-text',
                                        'flex items-center justify-center',
                                        'shadow',
                                      )}
                                      style={{
                                        backgroundColor: XYZ_COLORS[xyz],
                                        color: getBestContrastColor(
                                          XYZ_COLORS[xyz],
                                        ),
                                      }}
                                    >
                                      {xyz}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="">{xyz}</span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="text-2xl">
                          {Object.entries(getABCXYZMatrix(t)).map(
                            ([abc, row]) => (
                              <tr key={abc}>
                                <td
                                  className={cn(
                                    'relative text-center',
                                    'p-2',
                                    'h-12 w-8',
                                    'text-sm',
                                    matrixPosition.abc === abc
                                      ? 'bg-app-header-menu'
                                      : '',
                                  )}
                                >
                                  {matrixPosition.abc === abc ? (
                                    <div className="flex items-center justify-center">
                                      <div
                                        className={cn(
                                          'h-7 w-7',
                                          'rounded-full',
                                          // 'bg-menu-active text-menu-active-text',
                                          'flex items-center justify-center',
                                        )}
                                        style={{
                                          backgroundColor: ABC_COLORS[abc],
                                          color: getBestContrastColor(
                                            ABC_COLORS[abc],
                                          ),
                                        }}
                                      >
                                        {abc}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="">{abc}</span>
                                  )}
                                </td>
                                {Object.entries(row).map(([xyz, category]) => (
                                  <td
                                    key={xyz}
                                    className={cn(
                                      'border-app-panel/10 border',
                                      'text-center',
                                      'text-menu-active-text/50',
                                      'p-2',
                                      'h-12 w-12',
                                      matrixPosition.abc === abc &&
                                        matrixPosition.xyz === xyz
                                        ? 'border-3 text-menu-text border border-white' //ring-4 ring-white
                                        : '',
                                    )}
                                    style={{
                                      backgroundColor: ABC_XYZ_COLORS[category],
                                      opacity:
                                        matrixPosition.abc === abc &&
                                        matrixPosition.xyz === xyz
                                          ? 1
                                          : 0.3,
                                    }}
                                  >
                                    {category.charAt(0)}
                                  </td>
                                ))}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Stat>
              )}
            </header>

            {/* Classification Changes */}
            {accuracyMetrics && (
              <StatGroup
                // title={t`Classification Changes`}
                subTitle={t`Classification Metrics`}
                helpNavTo={'simulation/analyse/simulation-analyse'}
                title={
                  <>
                    {/* <span className="ltr:mr-1 rtl:ml-1">{t`Classification`}</span> */}
                    <Select
                      value={selectedMetric}
                      onChange={e =>
                        setSelectedMetric(e.target.value as MetricType)
                      }
                      className="w-48"
                    >
                      <option value="LINES">{t`Order Lines`}</option>
                      <option value="ABC">{t`Velocity: (ABC)`}</option>
                      <option value="XYZ">{t`Variability: (XYZ)`}</option>
                      <option value="COMBINED">{t`Inventory Class (ABC-XYZ)`}</option>
                      <option value="QUANTITIES">{t`Order Quantities`}</option>
                    </Select>
                  </>
                }
              >
                <StatGroup
                  className="!m-0"
                  classNameStatGroupItems="grid grid-cols-3 space-x-1 pt-0.5 pb-0 space-y-0"
                  horizontal
                >
                  <Stat
                    inFilterStat
                    title={t`Order Metrics`}
                    // isActionable
                    classChildren="pr-8"
                  >
                    <StatListItem
                      title={t`Lines Accuracy`}
                      value={formatInteger(accuracyMetrics.linesAccuracy)}
                      unitOfMeasure={`%`}
                    />
                    <StatListItem
                      title={t`Quantity Accuracy`}
                      value={formatInteger(accuracyMetrics.quantityAccuracy)}
                      unitOfMeasure={`%`}
                    />
                  </Stat>
                  <Stat
                    inFilterStat
                    title={t`Velocity: (ABC)`}
                    // isActionable
                    classChildren="pr-8"
                  >
                    <StatListItem
                      title={t`ABC Accuracy`}
                      value={formatInteger(accuracyMetrics.abcAccuracy)}
                      unitOfMeasure={`%`}
                    />
                    <StatListItem
                      title={t`ABC Differential Accuracy`}
                      value={formatInteger(
                        accuracyMetrics.abcDifferentialAccuracy,
                      )}
                      unitOfMeasure={`%`}
                    />
                  </Stat>

                  <Stat
                    inFilterStat
                    title={t`Variability: (XYZ)`}
                    // isActionable
                    classChildren="pr-8"
                  >
                    <StatListItem
                      title={t`XYZ Accuracy`}
                      value={formatInteger(accuracyMetrics.xyzAccuracy)}
                      unitOfMeasure={`%`}
                    />
                    <StatListItem
                      title={t`Combined ABC-XYZ`}
                      value={formatInteger(accuracyMetrics.combinedAccuracy)}
                      unitOfMeasure={`%`}
                    />
                  </Stat>
                </StatGroup>
              </StatGroup>
            )}

            <Stat title={t`Weekly Trends`} inPanelMode className="mb-1">
              {/* Line Chart */}
              <div
                //   className="bg-app-panel-dark"
                style={{ height: '400px' }}
              >
                <ResponsiveLine
                  data={lineChartData}
                  margin={{ top: 20, right: 50, bottom: 60, left: 50 }}
                  xScale={{ type: 'point' }}
                  yScale={{
                    type:
                      selectedMetric === 'LINES' ||
                      selectedMetric === 'QUANTITIES'
                        ? 'linear'
                        : 'point',
                    min: 'auto',
                    max: 'auto',
                  }}
                  curve="linear"
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                  }}
                  enablePoints={true}
                  pointSize={8}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  enableGridX={false}
                  enableGridY={true}
                  colors={{ scheme: 'category10' }}
                  lineWidth={2}
                  enableArea={false}
                  enableSlices="x"
                  useMesh={true}
                  legends={[
                    {
                      anchor: 'bottom',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: 50,
                      itemsSpacing: 20,
                      itemDirection: 'left-to-right',
                      itemWidth: 140,
                      itemHeight: 20,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      itemTextColor: 'rgba(255, 255, 255, 0.9)',
                    },
                  ]}
                  theme={{
                    axis: {
                      ticks: {
                        text: {
                          fill: 'rgba(255, 255, 255, 0.9)',
                        },
                      },
                    },
                    grid: {
                      line: {
                        stroke: 'rgba(162, 188, 204, 0.2)',
                      },
                    },
                    crosshair: {
                      line: {
                        stroke: 'rgba(255, 255, 255, 0.5)',
                        strokeWidth: 1,
                      },
                    },
                  }}
                />
              </div>
            </Stat>
            <Stat>
              <Table>
                <thead>
                  <tr>
                    <th className="p-2 text-left">{t`Week`}</th>
                    <th className="p-2 text-left">{t`Actual`}</th>
                    <th className="p-2 text-left">{t`Forecast`}</th>
                    <th className="p-2 text-left">{t`Error %`}</th>
                  </tr>
                </thead>
                <tbody>
                  {getWeeksFromData(selectedSKUData).map(week => (
                    <tr key={week} className="border-menu border border-t">
                      <td className="p-2">
                        {t`Week`} {week}
                      </td>
                      <td className="p-2">
                        {selectedMetric === 'ABC' && (
                          <span
                            className="rounded px-2 py-1"
                            style={{
                              backgroundColor:
                                ABC_COLORS[selectedSKUData[`w${week}_abc`]],
                            }}
                          >
                            {selectedSKUData[`w${week}_abc`]}
                          </span>
                        )}
                        {selectedMetric === 'XYZ' && (
                          <span
                            className="rounded px-2 py-1"
                            style={{
                              backgroundColor:
                                XYZ_COLORS[
                                  getXYZClassification(selectedSKUData, week)
                                ],
                            }}
                          >
                            {getXYZClassification(selectedSKUData, week)}
                          </span>
                        )}
                        {selectedMetric === 'COMBINED' && (
                          <span className="px-2 py-1">
                            {`${selectedSKUData[`w${week}_abc`]}-${getXYZClassification(selectedSKUData, week)}`}
                          </span>
                        )}
                        {selectedMetric === 'LINES' &&
                          selectedSKUData[`w${week}_lines`]}
                        {selectedMetric === 'QUANTITIES' &&
                          selectedSKUData[`w${week}_quantity`]}
                      </td>
                      <td className="p-2">
                        {selectedMetric === 'ABC' && (
                          <span
                            className="rounded px-2 py-1"
                            style={{
                              backgroundColor:
                                ABC_COLORS[selectedSKUData[`w${week}_fc_abc`]],
                            }}
                          >
                            {selectedSKUData[`w${week}_fc_abc`]}
                          </span>
                        )}
                        {selectedMetric === 'XYZ' && (
                          <span
                            className="rounded px-2 py-1"
                            style={{
                              backgroundColor:
                                XYZ_COLORS[
                                  getXYZClassification(
                                    selectedSKUData,
                                    week,
                                    true,
                                  )
                                ],
                            }}
                          >
                            {getXYZClassification(selectedSKUData, week, true)}
                          </span>
                        )}
                        {selectedMetric === 'COMBINED' && (
                          <span className="px-2 py-1">
                            {`${selectedSKUData[`w${week}_fc_abc`]}-${getXYZClassification(selectedSKUData, week, true)}`}
                          </span>
                        )}
                        {selectedMetric === 'LINES' &&
                          selectedSKUData[`w${week}_fc_lines`]}
                        {selectedMetric === 'QUANTITIES' &&
                          selectedSKUData[`w${week}_fc_quantity`]}
                      </td>
                      <td className="p-2">
                        {selectedMetric === 'ABC' &&
                          (selectedSKUData[`w${week}_abc`] ===
                          selectedSKUData[`w${week}_fc_abc`]
                            ? '0%'
                            : '100%')}
                        {selectedMetric === 'XYZ' &&
                          (getXYZClassification(selectedSKUData, week) ===
                          getXYZClassification(selectedSKUData, week, true)
                            ? '0%'
                            : '100%')}
                        {selectedMetric === 'COMBINED' &&
                          (selectedSKUData[`w${week}_abc`] ===
                            selectedSKUData[`w${week}_fc_abc`] &&
                          getXYZClassification(selectedSKUData, week) ===
                            getXYZClassification(selectedSKUData, week, true)
                            ? '0%'
                            : '100%')}
                        {selectedMetric === 'LINES' &&
                          (
                            (Math.abs(
                              selectedSKUData[`w${week}_lines`] -
                                selectedSKUData[`w${week}_fc_lines`],
                            ) /
                              accuracyMetrics.baselineLines) *
                            100
                          ).toFixed(1) + '%'}
                        {selectedMetric === 'QUANTITIES' &&
                          (
                            (Math.abs(
                              selectedSKUData[`w${week}_quantity`] -
                                selectedSKUData[`w${week}_fc_quantity`],
                            ) /
                              accuracyMetrics.baselineQty) *
                            100
                          ).toFixed(1) + '%'}
                      </td>
                    </tr>
                  ))}
                  {/* {(selectedMetric === 'LINES' ||
                    selectedMetric === 'QUANTITIES') &&
                    accuracyMetrics?.stats && (
                      <>
                        <tr className="border-app-panel-dark border border-t font-medium">
                          <td className="p-2">{t`Average`}</td>
                          <td className="p-2">
                            {selectedMetric === 'LINES'
                              ? accuracyMetrics.stats.lines.avg.toFixed(1)
                              : accuracyMetrics.stats.quantity.avg.toFixed(1)}
                          </td>
                          <td className="p-2">-</td>
                          <td className="p-2">-</td>
                        </tr>
                        <tr className="border-app-panel-dark border border-t font-medium">
                          <td className="p-2">{t`Min`}</td>
                          <td className="p-2">
                            {selectedMetric === 'LINES'
                              ? accuracyMetrics.stats.lines.min
                              : accuracyMetrics.stats.quantity.min}
                          </td>
                          <td className="p-2">-</td>
                          <td className="p-2">-</td>
                        </tr>
                        <tr className="border-app-panel-dark border border-t font-medium">
                          <td className="p-2">{t`Max`}</td>
                          <td className="p-2">
                            {selectedMetric === 'LINES'
                              ? accuracyMetrics.stats.lines.max
                              : accuracyMetrics.stats.quantity.max}
                          </td>
                          <td className="p-2">-</td>
                          <td className="p-2">-</td>
                        </tr>
                      </>
                    )} */}
                </tbody>
              </Table>
            </Stat>
          </>
        )}
      </div>
    </Container>
  );
};

export default SKUAnalysisView;
